// src/pages/sales/CustomersListPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
  useTheme,
  alpha,
  Link as MuiLink,
  Fade,
  Grid,
  Divider,
  Avatar,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  ReceiptLong as ReceiptIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  NoteAdd as NoteAddIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  MonetizationOn as MonetizationOnIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarTodayIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  marginLeft: theme.spacing(1),
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const CustomersListPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const [companyName, setCompanyName] = useState('');
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Customer detail dialog state
  const [customerDetailOpen, setCustomerDetailOpen] = useState(false);
  const [selectedCustomerForDetail, setSelectedCustomerForDetail] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  // Order history pagination
  const [orderPage, setOrderPage] = useState(0);
  const [orderRowsPerPage, setOrderRowsPerPage] = useState(10);

  // Format date without timezone issues
  const formatDate = (dateString, options = {}) => {
    if (!dateString) return 'Not set';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        // If options specify a format, use it
        if (options.month === 'short' || options.month === 'long') {
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          return date.toLocaleDateString('en-US', options);
        }
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + (dateString.includes('T') ? '' : 'T12:00:00'));
    if (options.month === 'short' || options.month === 'long') {
      return date.toLocaleDateString('en-US', options);
    }
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  useEffect(() => {
    fetchCustomers();
    fetchCompanyData();
  }, [companyId]);

  // Refetch when returning to this page (e.g., after navigating back from customer-new or customer-edit)
  useEffect(() => {
    const isOnCustomersListPage = location.pathname.includes('/customers-list');
    if (isOnCustomersListPage && customers.length === 0 && !loading) {
      // Only refetch if we're on the page, have no customers, and not currently loading
      fetchCustomers();
      fetchCompanyData();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (customers.length > 0) {
      applyFilters();
    } else if (customers.length === 0 && !loading) {
      // If customers is empty and not loading, ensure filteredCustomers is also empty
      setFilteredCustomers([]);
    }
  }, [customers, searchTerm]);



  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('company_id', companyId)
        .order('name');
      
      if (error) throw error;
      
      setCustomers(data || []);
      setFilteredCustomers(data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      if (error) throw error;

      if (data) {
        setCompanyName(data.name);
      }
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  const applyFilters = () => {
    let result = [...customers];
    
    // Apply search filter
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(customer => 
        (customer.name && customer.name.toLowerCase().includes(lowercasedSearch)) ||
        (customer.company && customer.company.toLowerCase().includes(lowercasedSearch)) ||
        (customer.email && customer.email.toLowerCase().includes(lowercasedSearch)) ||
        (customer.phone && customer.phone.toLowerCase().includes(lowercasedSearch))
      );
    }
    
    setFilteredCustomers(result);
    setPage(0); // Reset to first page when filtering
  };

  const handleOpenMenu = (event, customer) => {
    setSelectedCustomer(customer);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteCustomer = async () => {
    if (!selectedCustomer) return;
    
    // Check delete permission before proceeding
    if (!hasPermission('sales.customers.delete', 'delete')) {
      setError('You do not have permission to delete customers.');
      handleCloseDeleteDialog();
      return;
    }
    
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', selectedCustomer.id);
      
      if (error) throw error;
      
      // Remove from state
      const updatedCustomers = customers.filter(c => c.id !== selectedCustomer.id);
      setCustomers(updatedCustomers);
      setFilteredCustomers(filteredCustomers.filter(c => c.id !== selectedCustomer.id));
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting customer:', err);
      setError(`Failed to delete customer: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateInvoice = () => {
    if (!selectedCustomer) return;
    navigate(`/company/${companyId}/invoices/new`, { state: { customerId: selectedCustomer.id } });
    handleCloseMenu();
  };

  const handleEditCustomer = () => {
    if (!selectedCustomer) return;
    navigate(`/company/${companyId}/customer-edit/${selectedCustomer.id}`);
    handleCloseMenu();
  };

  const fetchCustomerOrders = async (customerId) => {
    setLoadingOrders(true);
    try {
      const { data, error } = await supabase
        .from('customer_purchase_orders')
        .select('id, po_number, order_date, status, total_amount, delivery_status')
        .eq('customer_id', customerId)
        .eq('company_id', companyId)
        .order('order_date', { ascending: false });
      
      if (error) throw error;
      
      setCustomerOrders(data || []);
    } catch (err) {
      console.error('Error fetching customer orders:', err);
      setCustomerOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleCustomerRowClick = (customer) => {
    setSelectedCustomerForDetail(customer);
    setCustomerDetailOpen(true);
    fetchCustomerOrders(customer.id);
  };

  const handleCloseCustomerDetail = () => {
    setCustomerDetailOpen(false);
    setSelectedCustomerForDetail(null);
    setCustomerOrders([]);
    setOrderPage(0);
    setOrderRowsPerPage(10);
  };

  const handleChangeOrderPage = (event, newPage) => {
    setOrderPage(newPage);
  };

  const handleChangeOrderRowsPerPage = (event) => {
    setOrderRowsPerPage(parseInt(event.target.value, 10));
    setOrderPage(0);
  };

  // COMPANY OWNER NOTE: Company owners/creators automatically have ALL permissions
  // To test permissions properly, use a different user account with limited role
  
  // Permission check - Sales Managers and Sales Reps can access
  if (!hasPermission('sales.customers.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{ 
            p: 8, 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <PeopleIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Customer Directory Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Customer Directory. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => window.history.back()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Fade in timeout={800}>
      <Container 
        maxWidth="xl"
        sx={{
          py: 3,
          pb: 8,
          px: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: '100vw !important'
        }}
      >
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2, color: 'black', '& .MuiAlert-message': { color: 'black' } }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        {/* Enhanced Page Header */}
        <Card sx={{
          borderRadius: 3,
          mb: 3,
          mt: 5,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: theme.palette.grey[100],
          backdropFilter: 'blur(20px)',
          boxShadow: 'none',
          borderTop: '3px solid #2196F3',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 20px ${alpha('#2196F3', 0.15)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
              <Box sx={{
                p: 2,
                borderRadius: 3,
                background: alpha('#2196F3', 0.08),
                border: `1px solid ${alpha('#2196F3', 0.15)}`,
              }}>
                <PeopleIcon sx={{ fontSize: 32, color: '#2196F3' }} />
              </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{
                fontWeight: 500,
                color: '#2196F3',
                mb: 0.5,
                fontSize: '2rem',
                letterSpacing: '-0.5px'
              }}>
                  Customer Directory
                </Typography>
              <Typography variant="body1" sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '1rem'
              }}>
                  {companyName || 'Loading...'}
                </Typography>
            </Box>
            {hasPermission('sales.customers.edit', 'write') && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/company/${companyId}/customer-new`)}
                sx={{ 
                  borderRadius: 2,
                  borderColor: 'grey.400',
                  color: 'primary.main',
                  borderWidth: 1,
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  background: 'white',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    borderColor: 'grey.600',
                    backgroundColor: 'white',
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                  }
                }}
              >
                Add Customer
              </Button>
            )}
          </Box>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary', 
              maxWidth: 700, 
              lineHeight: 1.5,
              fontSize: '0.95rem',
              fontWeight: 500
            }}>
              Browse and manage your customer database with powerful search and filtering tools for comprehensive customer relationship management.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search and Filter Bar */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(100%)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1,
            }
          },
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          }
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            placeholder="Search customers by name, company, email, or phone..."
            variant="outlined"
            size="medium"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '& fieldset': { border: 'none' },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '0.9rem',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }
            }}
          />
          <Button 
            startIcon={<RefreshIcon />}
            onClick={fetchCustomers}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              borderColor: 'grey.400',
              color: 'primary.main',
              borderWidth: 1,
              fontWeight: 500,
              px: 3,
              py: 1.5,
              background: 'white',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                borderColor: 'grey.600',
                backgroundColor: 'white',
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
              }
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Paper>

      {/* Customers Table */}
      {filteredCustomers.length === 0 ? (
        <Paper 
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Box sx={{ 
            p: 2.5, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            backdropFilter: 'blur(10px)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
            mb: 3,
            width: 'fit-content',
            mx: 'auto'
          }}>
            <PeopleIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            {customers.length === 0 ? 'No Customers Yet' : 'No Matching Customers'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto', fontWeight: 500 }}>
            {customers.length === 0 
              ? 'Add your first customer to start building your customer database.'
              : 'Try adjusting your search criteria.'}
          </Typography>
          {customers.length === 0 && hasPermission('sales.customers.edit', 'write') && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/company/${companyId}/customer-new`)}
              sx={{ 
                borderRadius: 2,
                borderColor: 'grey.400',
                color: 'primary.main',
                borderWidth: 1,
                fontWeight: 500,
                px: 3,
                py: 1.5,
                background: 'white',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'white',
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                }
              }}
            >
              Add Your First Customer
            </Button>
          )}
        </Paper>
      ) : (
        <Paper sx={{ 
          borderRadius: 3, 
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(100%)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1,
            }
          },
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          }
        }}>
          <TableContainer sx={{ overflow: 'visible' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Customer Name
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Company
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Email
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Phone
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Location
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Customer Since
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3,
                      width: 120
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? filteredCustomers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : filteredCustomers
                ).map((customer, index) => (
                  <TableRow 
                    key={customer.id} 
                    hover
                    onClick={() => handleCustomerRowClick(customer)}
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        transition: 'all 0.2s ease',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2.5,
                        px: 3
                      }
                    }}
                  >
                    {/* Customer Name */}
                    <TableCell>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.95rem'
                        }}
                      >
                        {customer.name}
                      </Typography>
                    </TableCell>
                    
                    {/* Company */}
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.9rem'
                        }}
                      >
                        {customer.company || 'No company'}
                      </Typography>
                    </TableCell>
                    
                    {/* Email */}
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.9rem'
                        }}
                      >
                        {customer.email || 'No email'}
                      </Typography>
                    </TableCell>
                    
                    {/* Phone */}
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.9rem'
                        }}
                      >
                        {customer.phone || 'No phone'}
                      </Typography>
                    </TableCell>
                    
                    {/* Location */}
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.9rem'
                        }}
                      >
                        {[customer.city, customer.state, customer.country].filter(Boolean).join(', ') || 'No location'}
                      </Typography>
                    </TableCell>
                    
                    {/* Customer Since */}
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.9rem'
                        }}
                      >
                        {formatDate(customer.customer_since, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </Typography>
                    </TableCell>
                    
                    {/* Actions */}
                    <TableCell align="center">
                      <Tooltip title="More options">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenMenu(e, customer);
                          }}
                          disabled={!hasPermission('sales.customers.edit', 'write') && !hasPermission('sales.customers.delete', 'delete') && !hasPermission('sales.invoices.edit', 'write')}
                          sx={{
                            color: (!hasPermission('sales.customers.edit', 'write') && !hasPermission('sales.customers.delete', 'delete') && !hasPermission('sales.invoices.edit', 'write')) ? 'text.disabled' : 'text.primary',
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                            borderRadius: 2,
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              bgcolor: (!hasPermission('sales.customers.edit', 'write') && !hasPermission('sales.customers.delete', 'delete') && !hasPermission('sales.invoices.edit', 'write')) ? 'transparent' : 'primary.main',
                              color: (!hasPermission('sales.customers.edit', 'write') && !hasPermission('sales.customers.delete', 'delete') && !hasPermission('sales.invoices.edit', 'write')) ? 'text.disabled' : 'white',
                              transform: (!hasPermission('sales.customers.edit', 'write') && !hasPermission('sales.customers.delete', 'delete') && !hasPermission('sales.invoices.edit', 'write')) ? 'none' : 'scale(1.05)',
                              boxShadow: (!hasPermission('sales.customers.edit', 'write') && !hasPermission('sales.customers.delete', 'delete') && !hasPermission('sales.invoices.edit', 'write')) ? 'none' : `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                            },
                            '&.Mui-disabled': {
                              color: 'text.disabled',
                              border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`
                            }
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ 
            borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderRadius: '0 0 12px 12px'
          }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredCustomers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ 
                px: 3,
                py: 2,
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: theme.palette.text.primary
                },
                '& .MuiTablePagination-select': {
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                },
                '& .MuiTablePagination-actions': {
                  '& .MuiIconButton-root': {
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    borderRadius: 2,
                    margin: '0 2px',
                    background: 'white',
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: theme.palette.primary.main,
                      color: 'white',
                      transform: 'translateY(-1px)',
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                    },
                    '&.Mui-disabled': {
                      background: alpha(theme.palette.grey[300], 0.5),
                      color: theme.palette.grey[500],
                      border: `1px solid ${theme.palette.grey[300]}`,
                      transform: 'none',
                      boxShadow: 'none'
                    }
                  }
                }
              }}
            />
          </Box>
        </Paper>
      )}

      {/* Customer Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.grey[500], 0.2)}`,
            minWidth: 180
          }
        }}
      >
        <MenuItem 
          onClick={hasPermission('sales.customers.edit', 'write') ? handleEditCustomer : undefined} 
          disabled={!hasPermission('sales.customers.edit', 'write')}
          sx={{ py: 1.5, px: 2 }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" color={hasPermission('sales.customers.edit', 'write') ? "primary" : "disabled"} />
          </ListItemIcon>
          <ListItemText sx={{ color: hasPermission('sales.customers.edit', 'write') ? 'text.primary' : 'text.disabled' }}>
            Edit Customer
          </ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={hasPermission('sales.invoices.edit', 'write') ? handleCreateInvoice : undefined} 
          disabled={!hasPermission('sales.invoices.edit', 'write')}
          sx={{ py: 1.5, px: 2 }}
        >
          <ListItemIcon>
            <NoteAddIcon fontSize="small" color={hasPermission('sales.invoices.edit', 'write') ? "success" : "disabled"} />
          </ListItemIcon>
          <ListItemText sx={{ color: hasPermission('sales.invoices.edit', 'write') ? 'text.primary' : 'text.disabled' }}>
            Create Invoice
          </ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={hasPermission('sales.customers.delete', 'delete') ? handleOpenDeleteDialog : undefined} 
          disabled={!hasPermission('sales.customers.delete', 'delete')}
          sx={{ py: 1.5, px: 2 }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color={hasPermission('sales.customers.delete', 'delete') ? "error" : "disabled"} />
          </ListItemIcon>
          <ListItemText sx={{ color: hasPermission('sales.customers.delete', 'delete') ? 'error.main' : 'text.disabled' }}>
            Delete Customer
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: { 
            borderRadius: 4,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 20px 40px ${alpha(theme.palette.error.main, 0.15)}`
          }
        }}
      >
        <DialogTitle sx={{ 
          background: theme.palette.error.main,
          color: 'white',
          p: 3,
          fontWeight: 500
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <DeleteIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.2rem' }}>
              Delete Customer
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 6 }}>
          <DialogContentText sx={{ fontSize: '1rem', mt: 2, fontWeight: 500 }}>
            Are you sure you want to delete <strong>{selectedCustomer?.name}</strong>? This action cannot be undone and will remove all associated data.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseDeleteDialog} 
            disabled={deleting}
            sx={{ 
              borderRadius: 3,
              fontSize: '0.9rem',
              fontWeight: 500,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              color: 'text.primary',
              '&:hover': {
                bgcolor: alpha(theme.palette.grey[100], 0.8),
                border: `1px solid ${theme.palette.primary.main}`
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteCustomer} 
            color="error"
            variant="contained"
            disabled={deleting}
            sx={{ 
              borderRadius: 3,
              fontSize: '0.9rem',
              fontWeight: 500,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.error.main}`,
                boxShadow: `0 6px 16px ${alpha(theme.palette.error.main, 0.3)}`
              }
            }}
          >
            {deleting ? 'Deleting...' : 'Delete Customer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer Detail Dialog */}
      <Dialog
        open={customerDetailOpen}
        onClose={handleCloseCustomerDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            maxHeight: '90vh',
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
            bgcolor: alpha(theme.palette.grey[50], 0.95),
            backdropFilter: 'blur(10px)',
            overflow: 'hidden',
            userSelect: 'text',
            '& *': {
              userSelect: 'text'
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          pt: 3,
          px: 3,
          fontSize: '1.25rem', 
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <PersonIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
                Customer Details
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
                {selectedCustomerForDetail?.name}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={handleCloseCustomerDetail} 
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.grey[400], 0.1),
                color: 'text.primary'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          userSelect: 'text',
          '& *': {
            userSelect: 'text'
          }
        }}>
          {selectedCustomerForDetail && (
            <Box sx={{ 
              p: 4,
              userSelect: 'text',
              '& *': {
                userSelect: 'text'
              }
            }}>
              <Grid container spacing={4}>
                {/* Basic Information Card */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    height: '100%',
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    minHeight: '220px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    userSelect: 'text',
                    '& *': {
                      userSelect: 'text'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                        Basic Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Full Name
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedCustomerForDetail.name || 'Not provided'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Company
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedCustomerForDetail.company || 'Not provided'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Contact Information Card */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    height: '100%',
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    minHeight: '220px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    userSelect: 'text',
                    '& *': {
                      userSelect: 'text'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                        Contact Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Email Address
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          fontWeight: 500,
                          wordBreak: 'break-word'
                        }}>
                          {selectedCustomerForDetail.email || 'Not provided'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Phone Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedCustomerForDetail.phone || 'Not provided'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Address Information Card */}
                <Grid item xs={12}>
                  <Card sx={{ 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    userSelect: 'text',
                    '& *': {
                      userSelect: 'text'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                        Address Information
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Street Address
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {selectedCustomerForDetail.street || 'Not provided'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              City
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {selectedCustomerForDetail.city || 'Not provided'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              State/Province
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {selectedCustomerForDetail.state || 'Not provided'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              ZIP/Postal Code
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {selectedCustomerForDetail.zip_code || 'Not provided'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Country
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {selectedCustomerForDetail.country || 'Not provided'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Customer Since and Payment Terms Card */}
                <Grid item xs={12}>
                  <Card sx={{ 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    userSelect: 'text',
                    '& *': {
                      userSelect: 'text'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                        Customer Details
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Customer Since Date
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {formatDate(selectedCustomerForDetail.customer_since, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Record Created
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {formatDate(selectedCustomerForDetail.created_at, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                              Payment Terms
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {selectedCustomerForDetail.payment_terms || 'Not specified'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Order History Card */}
                <Grid item xs={12}>
                  <Card sx={{ 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    userSelect: 'text',
                    '& *': {
                      userSelect: 'text'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                        Order History
                      </Typography>
                      {loadingOrders ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress size={32} />
                        </Box>
                      ) : customerOrders.length === 0 ? (
                        <Box sx={{ 
                          textAlign: 'center', 
                          py: 4,
                          border: '2px dashed',
                          borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.grey[100], 0.5)
                        }}>
                          <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            No orders found for this customer
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 500, color: 'primary.main' }}>PI Number</TableCell>
                                  <TableCell sx={{ fontWeight: 500, color: 'primary.main' }}>Order Date</TableCell>
                                  <TableCell sx={{ fontWeight: 500, color: 'primary.main' }}>Status</TableCell>
                                  <TableCell sx={{ fontWeight: 500, color: 'primary.main' }}>Delivery Status</TableCell>
                                  <TableCell sx={{ fontWeight: 500, color: 'primary.main' }} align="right">Total Amount</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(orderRowsPerPage > 0
                                  ? customerOrders.slice(orderPage * orderRowsPerPage, orderPage * orderRowsPerPage + orderRowsPerPage)
                                  : customerOrders
                                ).map((order) => (
                                  <TableRow 
                                    key={order.id}
                                    hover
                                    sx={{
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                                      }
                                    }}
                                  >
                                    <TableCell sx={{ fontWeight: 500 }}>{order.po_number}</TableCell>
                                    <TableCell>
                                      {formatDate(order.order_date, {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ 
                                        display: 'inline-block',
                                        px: 2, 
                                        py: 0.5, 
                                        borderRadius: 1,
                                        bgcolor: 
                                          order.status === 'delivered' ? alpha(theme.palette.success.main, 0.1) :
                                          order.status === 'in_production' ? alpha(theme.palette.info.main, 0.1) :
                                          order.status === 'approved' ? alpha(theme.palette.primary.main, 0.1) :
                                          order.status === 'cancelled' ? alpha(theme.palette.error.main, 0.1) :
                                          alpha(theme.palette.grey[400], 0.1),
                                        color:
                                          order.status === 'delivered' ? theme.palette.success.dark :
                                          order.status === 'in_production' ? theme.palette.info.dark :
                                          order.status === 'approved' ? theme.palette.primary.dark :
                                          order.status === 'cancelled' ? theme.palette.error.dark :
                                          theme.palette.text.primary,
                                        fontWeight: 500,
                                        fontSize: '0.85rem',
                                        textTransform: 'capitalize'
                                      }}>
                                        {order.status.replace(/_/g, ' ')}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ 
                                        display: 'inline-block',
                                        px: 2, 
                                        py: 0.5, 
                                        borderRadius: 1,
                                        bgcolor: 
                                          order.delivery_status === 'delivered' || order.delivery_status === 'confirmed' ? alpha(theme.palette.success.main, 0.1) :
                                          order.delivery_status === 'in_transit' ? alpha(theme.palette.info.main, 0.1) :
                                          order.delivery_status === 'issues_reported' ? alpha(theme.palette.error.main, 0.1) :
                                          alpha(theme.palette.grey[400], 0.1),
                                        color:
                                          order.delivery_status === 'delivered' || order.delivery_status === 'confirmed' ? theme.palette.success.dark :
                                          order.delivery_status === 'in_transit' ? theme.palette.info.dark :
                                          order.delivery_status === 'issues_reported' ? theme.palette.error.dark :
                                          theme.palette.text.primary,
                                        fontWeight: 500,
                                        fontSize: '0.85rem',
                                        textTransform: 'capitalize'
                                      }}>
                                        {order.delivery_status ? order.delivery_status.replace(/_/g, ' ') : 'Pending'}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                                      ${order.total_amount ? Number(order.total_amount).toFixed(2) : '0.00'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <Box sx={{ 
                            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                            mt: 2
                          }}>
                            <TablePagination
                              rowsPerPageOptions={[10, 25, 50]}
                              component="div"
                              count={customerOrders.length}
                              rowsPerPage={orderRowsPerPage}
                              page={orderPage}
                              onPageChange={handleChangeOrderPage}
                              onRowsPerPageChange={handleChangeOrderRowsPerPage}
                              sx={{ 
                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                  color: theme.palette.text.primary
                                },
                                '& .MuiTablePagination-select': {
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  borderRadius: 2,
                                  px: 2,
                                  py: 0.5,
                                  fontWeight: 500,
                                  fontSize: '0.875rem',
                                  background: 'white',
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  }
                                },
                                '& .MuiTablePagination-actions': {
                                  '& .MuiIconButton-root': {
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                    borderRadius: 2,
                                    margin: '0 2px',
                                    background: 'white',
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      background: theme.palette.primary.main,
                                      color: 'white',
                                      transform: 'translateY(-1px)',
                                      border: `1px solid ${theme.palette.primary.main}`,
                                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                                    },
                                    '&.Mui-disabled': {
                                      background: alpha(theme.palette.grey[300], 0.5),
                                      color: theme.palette.grey[500],
                                      border: `1px solid ${theme.palette.grey[300]}`,
                                      transform: 'none',
                                      boxShadow: 'none'
                                    }
                                  }
                                }
                              }}
                            />
                          </Box>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          pt: 2,
          background: 'white',
          borderTop: '1px solid',
          borderColor: alpha(theme.palette.grey[300], 0.5)
        }}>
          <Button 
            onClick={handleCloseCustomerDetail}
            sx={{ 
              borderRadius: 3,
              fontSize: '0.9rem',
              fontWeight: 500,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              color: 'text.primary',
              px: 3,
              py: 1.5,
              '&:hover': {
                bgcolor: alpha(theme.palette.grey[100], 0.8),
                border: `1px solid ${theme.palette.primary.main}`,
                transform: 'translateY(-1px)'
              }
            }}
          >
            Close
          </Button>
          {hasPermission('sales.customers.edit', 'write') && (
            <Button 
              onClick={() => {
                navigate(`/company/${companyId}/customer-edit/${selectedCustomerForDetail.id}`);
                handleCloseCustomerDetail();
              }}
              variant="contained"
              startIcon={<EditIcon />}
              sx={{ 
                borderRadius: 3,
                fontSize: '0.9rem',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                px: 3,
                py: 1.5,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            >
              Edit Customer
            </Button>
          )}
        </DialogActions>
      </Dialog>
        </Container>
    </Fade>
  );
};

export default CustomersListPage;